/**
 * MER-483 finding 3: per-row derived-ratio fill for table widgets.
 *
 * Route tables bind gross_margin_pct, but the pipeline's MER-453
 * denominator guard intentionally nulls the STORED ratio for rows it
 * cannot anchor, so the tables rendered "-" for nearly every route
 * while the CM% chart (which computes SUM(num)/SUM(denom) from the
 * dollar columns via DERIVED_RATIOS) showed values for the same
 * routes on the same page.
 *
 * This fills a declared ratio column from the SAME dollar building
 * blocks, row by row, ONLY where the stored value is null. Stored
 * values always win (route 206's pipeline-anchored 78.3% stays
 * exactly as written). Un-anchorable rows (null or zero denominator)
 * stay null and keep rendering "-".
 *
 * Tables have NO cell-level sanity clamp (unlike charts, which null
 * points beyond +/-200% in ChartWidget.gatePoint), so the fill clamps
 * itself: fraction-convention columns (_pct / _rate suffix, stored as
 * fractions per the MER-441 rule where 2.0 = 200%) whose computed
 * value lands outside +/-PCT_BAND stay null and render "-". This is
 * exactly the garbage class the pipeline's stored-null guard
 * suppressed; the fill must not resurrect it. Non-fraction ratios
 * (e.g. arpc, dollars per customer) are unbounded.
 *
 * Plain .mjs so the node:test suite imports it directly (the
 * telemetryQueue.mjs precedent). The registry is passed in by the
 * caller (DERIVED_RATIOS from lib/queries.ts) so this stays pure.
 */

/**
 * @param {Array<Record<string, unknown>>} rows   raw table rows
 * @param {string | null} table   resolved source table (e.g. "route_kpis")
 * @param {ReadonlyArray<string>} declaredColumns   the widget's columns allow-list
 * @param {Record<string, { table: string, num: string, denom: string }>} registry
 * @returns {Array<Record<string, unknown>>}   new array; rows are shallow
 *   copies only when a fill happened, untouched otherwise.
 */
/** Mirrors PCT_DISPLAY_LIMIT in lib/sanityGate.ts: fractions, 2.0 = 200%. */
const PCT_BAND = 2.0;

const FRACTION_COLUMN_RE = /(_pct|_rate)$/;

export function fillDerivedRatioColumns(rows, table, declaredColumns, registry) {
  if (!table || !Array.isArray(rows) || rows.length === 0) return rows;
  const targets = declaredColumns.filter((c) => registry[c] && registry[c].table === table);
  if (targets.length === 0) return rows;

  return rows.map((row) => {
    let out = row;
    for (const col of targets) {
      const stored = row[col];
      if (stored !== null && stored !== undefined) continue;
      const { num, denom } = registry[col];
      const n = toFiniteNumber(row[num]);
      const d = toFiniteNumber(row[denom]);
      if (n === null || d === null || d === 0) continue;
      const ratio = n / d;
      if (FRACTION_COLUMN_RE.test(col) && Math.abs(ratio) > PCT_BAND) continue;
      if (out === row) out = { ...row };
      out[col] = ratio;
    }
    return out;
  });
}

/** numeric columns can surface as number or numeric-string. */
function toFiniteNumber(value) {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
